import { createRouter, createWebHistory } from 'vue-router';
import { getCurrentUser } from 'vuefire';
import { isNative } from '../lib/platform';
import { safeNextPath } from '../lib/navigation';
import { defaultSignedInRouteName, isPartnerOnlyClaims, partnerIdsFromClaims } from '../lib/access';

export const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
  routes: [
    { path: '/', name: 'home', component: () => import('../pages/marketing/Landing.vue') },
    { path: '/contact', name: 'contact', component: () => import('../pages/marketing/Contact.vue') },
    { path: '/partner', alias: '/partner/', name: 'partner', component: () => import('../pages/marketing/Partner.vue') },
    { path: '/partner/scan', name: 'partner-scan', component: () => import('../pages/marketing/PartnerQrHub.vue') },
    { path: '/partner/activate', name: 'partner-activate', component: () => import('../pages/marketing/PartnerActivate.vue') },
    { path: '/partner/invite', name: 'partner-invite', component: () => import('../pages/marketing/PartnerInvite.vue') },
    { path: '/partner/intake', name: 'partner-intake', meta: { requiresPartnerIntake: true }, component: () => import('../pages/app/PartnerIntake.vue') },
    { path: '/partner/inventory', name: 'partner-qr-sets', meta: { requiresPartner: true }, component: () => import('../pages/app/PartnerQrSets.vue') },
    { path: '/partner/labels', redirect: { name: 'partner-qr-sets' } },
    { path: '/partner/labels/:pathMatch(.*)*', redirect: { name: 'partner-qr-sets' } },
    { path: '/partner/shipments', name: 'partner-shipments', meta: { requiresPartner: true }, component: () => import('../pages/app/PartnerShipments.vue') },
    { path: '/gift', name: 'gift', component: () => import('../pages/marketing/GiftCheckout.vue') },
    { path: '/gift/certificate', name: 'gift-certificate', component: () => import('../pages/marketing/GiftCertificate.vue') },
    { path: '/gift/claim', name: 'gift-claim', component: () => import('../pages/marketing/GiftClaim.vue') },
    { path: '/login', name: 'login', meta: { guestOnly: true }, component: () => import('../pages/auth/Login.vue') },
    { path: '/register', name: 'register', meta: { guestOnly: true }, component: () => import('../pages/auth/Register.vue') },
    { path: '/forgot-password', name: 'forgot', meta: { guestOnly: true }, component: () => import('../pages/auth/ForgotPassword.vue') },
    { path: '/verify-email', name: 'verify-email', meta: { requiresAuth: true }, component: () => import('../pages/auth/VerifyEmail.vue') },
    { path: '/pets', name: 'pets', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/Pets.vue') },
    { path: '/reserve', name: 'reserve', meta: { customerOnly: true }, component: () => import('../pages/app/Reserve.vue') },
    { path: '/reserve/finish', name: 'reserve-finish', component: () => import('../pages/marketing/ReserveFinish.vue') },
    { path: '/pets/:id/checkout', name: 'pet-checkout', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/ReserveReview.vue') },
    { path: '/alerts', name: 'alerts', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/Alerts.vue') },
    { path: '/pets/:id', name: 'pet', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/PetProfile.vue') },
    { path: '/pets/:id/milestones', name: 'pet-milestones', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/PetMilestones.vue') },
    { path: '/pets/:id/edit', name: 'pet-edit', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/PetEdit.vue') },
    { path: '/pets/:id/sky', name: 'pet-sky', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/PetSky.vue') },
    { path: '/crew', name: 'crew', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/Crew.vue') },
    { path: '/account', name: 'account', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/Account.vue') },
    { path: '/kit/:token', name: 'kit-qr', meta: { requiresAuth: true, customerOnly: true }, component: () => import('../pages/app/KitQrResolve.vue') },
    { path: '/admin', name: 'admin', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminPets.vue') },
    { path: '/admin/pets/:id', name: 'admin-pet', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminPetDetail.vue') },
    { path: '/admin/pets/:id/kit-label', name: 'admin-kit-label', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminKitQrLabel.vue') },
    { path: '/admin/missions', name: 'admin-missions', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminMissions.vue') },
    { path: '/admin/users', name: 'admin-users', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminUsers.vue') },
    { path: '/admin/promos', name: 'admin-promos', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminPromos.vue') },
    { path: '/admin/partners', name: 'admin-partners', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminPartners.vue') },
    { path: '/admin/partner-qr', name: 'admin-partner-qr', meta: { requiresAdmin: true }, component: () => import('../pages/admin/PartnerQrInventory.vue') },
    { path: '/admin/partner-shipments', name: 'admin-partner-shipments', meta: { requiresAdmin: true }, component: () => import('../pages/admin/AdminPartnerShipments.vue') },
    { path: '/admin/partner-resolutions', name: 'admin-partner-resolutions', meta: { requiresAdmin: true }, component: () => import('../pages/admin/PartnerQrResolutions.vue') },
    { path: '/admin/partner-qr/:batchId/certificates', name: 'admin-partner-qr-certificates', meta: { requiresAdmin: true }, component: () => import('../pages/admin/PartnerQrCertificates.vue') },
    { path: '/admin/partner-qr/:batchId/labels', name: 'admin-partner-qr-labels', meta: { requiresAdmin: true }, component: () => import('../pages/admin/PartnerQrLabels.vue') },
    { path: '/access-denied', name: 'access-denied', component: () => import('../pages/AccessDenied.vue') },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('../pages/NotFound.vue') },
  ],
});

// After a new deploy, an open tab still references the previous build's lazy
// chunk hashes. When one fails to load, reload once to pick up the new bundle.
router.onError((error, to) => {
  const message = error instanceof Error ? error.message : '';
  if (/dynamically imported module|module script failed|Failed to fetch/i.test(message)) {
    const key = 'chunk-reload-at';
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.assign(to.fullPath);
    }
  }
});

router.beforeEach(async (to) => {
  const user = await getCurrentUser();
  const token = user ? await user.getIdTokenResult() : null;
  const claims = token?.claims ?? {};
  const hasPartnerAccess = partnerIdsFromClaims(claims).length > 0;
  const isPartnerOnly = isPartnerOnlyClaims(claims);

  if (to.path === '/' && isNative) return user ? { name: defaultSignedInRouteName(claims) } : { name: 'login' };

  const needsAuth = to.meta.requiresAuth || to.meta.requiresAdmin || to.meta.requiresPartner || to.meta.requiresPartnerIntake;
  if (needsAuth && !user) return { name: 'login', query: { next: to.fullPath } };

  // An account is only usable once its email is confirmed. Confine an unverified
  // signed-in user to the verify-email page (and keep them off auth screens),
  // while leaving public marketing pages and password reset reachable.
  if (user && !user.emailVerified && to.name !== 'verify-email') {
    if (needsAuth) return { name: 'verify-email', query: { next: to.fullPath } };
    if (to.meta.guestOnly) return { name: 'verify-email' };
  }
  if (to.name === 'verify-email' && (!user || user.emailVerified)) {
    return user ? safeNextPath(to.query.next) || { name: defaultSignedInRouteName(claims) } : { name: 'login' };
  }

  if (to.meta.guestOnly && user && user.emailVerified) return safeNextPath(to.query.next) || { name: defaultSignedInRouteName(claims) };
  if (to.meta.customerOnly && isPartnerOnly) return { name: 'partner-qr-sets' };
  if (to.meta.requiresAdmin && user) {
    if (claims.admin !== true) return { name: 'access-denied' };
  }
  if ((to.meta.requiresPartner || to.meta.requiresPartnerIntake) && user) {
    if (to.meta.requiresPartner && !hasPartnerAccess) return { name: 'access-denied' };
    if (to.meta.requiresPartnerIntake && claims.admin !== true && !hasPartnerAccess) return { name: 'access-denied' };
    if ('partnerId' in to.query) {
      const partnerId = to.query.partnerId;
      if (typeof partnerId !== 'string' || !partnerId.trim() || (claims.admin !== true && !partnerIdsFromClaims(claims).includes(partnerId))) {
        return { name: 'access-denied' };
      }
    }
  }
  return true;
});
